/**
 * FormSection — 詳情頁/表單頁的「區塊分組」（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 靈感：Stripe / Linear 設置頁分區風格
 *   - 左列：標題 + 說明（固定寬 220px）
 *   - 右列：表單內容（flex:1）
 *   - 可選 stepNumber：多步驟表單
 *   - bordered：啟用底部 1px 分割線（連續 FormSection 場景）
 *   - layout=stacked：傳統「標題在上、內容在下」布局
 *
 * 使用：
 *   <FormSection
 *     title="基本資訊"
 *     description="驗證組織的核心識別信息"
 *     layout="sidebar"
 *   >
 *     <Form>...</Form>
 *   </FormSection>
 */
import type { ReactNode } from 'react';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';

export interface FormSectionProps {
  /** 區段標題 */
  title: ReactNode;
  /** 區段說明（用於側邊欄布局時顯示在標題下） */
  description?: ReactNode;
  /** 步驟編號（如 "01"、"1"），顯示在標題前 */
  stepNumber?: ReactNode;
  /** 主內容 */
  children: ReactNode;
  /** 右上操作區 */
  actions?: ReactNode;
  /** 是否帶底部分割線（連續 FormSection 時使用） */
  bordered?: boolean;
  /**
   * 布局模式：
   *   stacked  = 標題在上、內容在下（默認，適合 Drawer / 緊湊頁）
   *   sidebar  = 標題左列 + 內容右列（適合寬頁面的設置分區）
   */
  layout?: 'stacked' | 'sidebar';
}

export default function FormSection({
  title,
  description,
  stepNumber,
  children,
  actions,
  bordered = false,
  layout = 'stacked',
}: FormSectionProps) {
  const isSidebar = layout === 'sidebar';

  return (
    <section
      style={{
        paddingBottom: bordered ? space[6] : 0,
        borderBottom: bordered ? `1px solid ${color.border.subtle}` : 'none',
        marginBottom: bordered ? space[6] : space[5],
        display: isSidebar ? 'flex' : 'block',
        gap: isSidebar ? space[8] : undefined,
        alignItems: isSidebar ? 'flex-start' : undefined,
      }}
    >
      {/* 標題區（sidebar 模式時獨立左列，stacked 時全寬） */}
      <div
        style={{
          width: isSidebar ? 220 : 'auto',
          flexShrink: isSidebar ? 0 : undefined,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: isSidebar ? 0 : space[4],
          gap: space[3],
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: space[3], minWidth: 0 }}>
          {/* 步驟編號 badge */}
          {stepNumber !== undefined && (
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: radius.md,
                background: color.brand.primarySoft,
                color: color.brand.primary,
                border: `1px solid rgba(26, 133, 73, 0.20)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: font.mono,
                fontSize: text.xs,
                fontWeight: weight.bold,
                flexShrink: 0,
                marginTop: 2,
                letterSpacing: tracking.wider,
              }}
            >
              {stepNumber}
            </div>
          )}

          <div style={{ minWidth: 0 }}>
            {/* 標題：Onest display，weight 600 */}
            <h3
              style={{
                margin: 0,
                fontFamily: font.display,
                fontSize: isSidebar ? text.md : text.lg,
                fontWeight: weight.semibold,
                color: color.text.primary,
                letterSpacing: '-0.01em',
                lineHeight: leading.tight,
              }}
            >
              {title}
            </h3>
            {/* 說明：stone-500 弱化，max-width 限制 */}
            {description && (
              <p
                style={{
                  margin: 0,
                  marginTop: 4,
                  fontSize: text.sm,
                  color: color.text.tertiary,
                  lineHeight: leading.snug,
                  maxWidth: isSidebar ? 190 : 560,
                }}
              >
                {description}
              </p>
            )}
          </div>
        </div>

        {/* actions（非 sidebar 模式時在標題行右側） */}
        {actions && !isSidebar && (
          <div style={{ display: 'flex', alignItems: 'center', gap: space[2], flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>

      {/* 內容區 */}
      <div style={{ flex: isSidebar ? 1 : undefined, minWidth: 0 }}>
        {children}
        {/* sidebar 模式的 actions 放在內容底部右側 */}
        {actions && isSidebar && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: space[4],
              gap: space[2],
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </section>
  );
}
